import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Wrench, ArrowLeft, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export function useProductPaused(productKey) {
  const [state, setState] = useState({ loading: true, paused: false, message: "", since: null });
  useEffect(() => {
    let alive = true;
    const token = typeof window !== "undefined" ? localStorage.getItem("tdf_user_token") : null;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    axios.get(`${API}/products/availability`, { headers })
      .then((r) => {
        if (alive) setState({
          loading: false,
          paused: (r.data?.paused || []).includes(productKey),
          message: r.data?.message || "",
          since: r.data?.details?.[productKey]?.since || null,
        });
      })
      .catch(() => { if (alive) setState({ loading: false, paused: false, message: "", since: null }); });
    return () => { alive = false; };
  }, [productKey]);
  return state;
}

const pad2 = (n) => String(n).padStart(2, "0");

export function formatElapsed(sinceIso) {
  const ms = Date.now() - new Date(sinceIso).getTime();
  if (!sinceIso || Number.isNaN(ms) || ms < 0) return null;
  let secs = Math.floor(ms / 1000);
  const months = Math.floor(secs / (30 * 86400)); secs -= months * 30 * 86400;
  const days = Math.floor(secs / 86400); secs -= days * 86400;
  const hours = Math.floor(secs / 3600); secs -= hours * 3600;
  const mins = Math.floor(secs / 60); secs -= mins * 60;
  const parts = [];
  if (months) parts.push(`${months}mo`);
  if (days || parts.length) parts.push(`${days}d`);
  if (hours || parts.length) parts.push(`${hours}h`);
  parts.push(`${pad2(mins)}m`);
  parts.push(`${pad2(secs)}s`);
  return parts.join(" ");
}

function ElapsedTicker({ since }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const label = formatElapsed(since);
  if (!label) return null;
  return <span className="font-mono font-extrabold tabular-nums whitespace-nowrap">{label}</span>;
}

/**
 * Small pre-purchase notice for sales pages. Renders nothing unless the
 * product is currently paused for maintenance. Purchases stay enabled —
 * this simply lets the customer choose to buy now or wait.
 */
export function SalesMaintenanceBanner({ productKey, productLabel, className = "" }) {
  const { loading, paused, since } = useProductPaused(productKey);
  if (loading || !paused) return null;
  return (
    <div
      className={`rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 flex items-start gap-3 ${className}`}
      data-testid={`sales-maintenance-banner-${productKey}`}
    >
      <Wrench className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
      <p className="leading-relaxed">
        <span className="font-extrabold">{productLabel} is temporarily under maintenance</span> — it has been
        offline for {since ? <ElapsedTicker since={since} /> : "a short while"} and will be back online shortly.
        You can still purchase now and your access will be ready the moment it returns — or feel free to check
        back a little later.
      </p>
    </div>
  );
}


export function MaintenanceNotice({ productLabel, message }) {
  return (
    <section className="bg-slate-50 py-16 md:py-24 min-h-[60vh]" data-testid="maintenance-notice">
      <div className="max-w-2xl mx-auto px-5 lg:px-8 text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-100 text-amber-700 mb-5">
          <Wrench className="w-6 h-6" />
        </div>
        <p className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1 mb-4">
          <Clock className="w-3 h-3" /> Temporary — back soon
        </p>
        <h1 className="text-3xl md:text-4xl font-extrabold text-[#0F1B2E] mb-3">
          {productLabel} is briefly down for maintenance
        </h1>
        <p className="text-slate-600 mb-8 leading-relaxed">
          {message || "This product is temporarily offline while we carry out scheduled maintenance. Your access and all of your progress are completely safe — nothing has been lost. We expect to be back online shortly, so please check back in a little while."}
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link
            to="/my-account"
            className="rounded-full bg-[#E63B2E] hover:bg-[#c92f24] text-white font-bold px-6 py-3 text-sm inline-flex items-center gap-1.5"
            data-testid="maintenance-back-to-account"
          >
            <ArrowLeft className="w-4 h-4" /> Back to my account
          </Link>
          <Link
            to="/contact"
            className="rounded-full bg-white border border-slate-300 hover:bg-slate-100 text-slate-700 font-bold px-6 py-3 text-sm"
            data-testid="maintenance-contact"
          >
            Contact us
          </Link>
        </div>
      </div>
    </section>
  );
}
